import { createSlice } from '@reduxjs/toolkit';

const USER_KEY = 'auth:user';
const ACCESS_KEY = 'auth:accessToken';
const REFRESH_KEY = 'auth:refreshToken';

// Web shim is detected by the absence of window.electron.db. On web, auth
// tokens live in HttpOnly cookies — we MUST NOT mirror them in localStorage,
// because that's exactly the XSS surface the cookie migration was meant to
// close. The user identity is read instead from the non-HttpOnly mg_user
// cookie so SSR + initial render both have a hint to render the right UI.
const IS_WEB = typeof window !== 'undefined' && window.electron && !window.electron.db;

function readUserFromCookie() {
    if (typeof document === 'undefined') return null;
    const m = document.cookie.match(/(?:^|;\s*)mg_user=([^;]+)/);
    if (!m) return null;
    try {
        const json = atob(decodeURIComponent(m[1]));
        const parsed = JSON.parse(json);
        if (!parsed?.id || !parsed?.email) return null;
        return parsed;
    } catch {
        return null;
    }
}

// Hydrate from localStorage (Electron) or mg_user cookie (web) so the app can
// render the authenticated UI offline / during an outage. `checkAuthStatus`
// will still run and refresh server-side state, but only *explicit* 401/403
// on refresh ever logs out.
//
// Invariant: `isAuthenticated` requires a cached user. If we have tokens but
// no user data, we can't render the profile correctly — fall back to login.
function hydrateInitialState() {
    if (IS_WEB) {
        const user = readUserFromCookie();
        const isAuthenticated = !!user;
        return {
            isAuthenticated,
            user,
            // Tokens never live in JS on web — cookies handle auth.
            accessToken: null,
            refreshToken: null,
            isLoading: !isAuthenticated,
            error: null,
            showLoginModal: !isAuthenticated,
            showRegisterModal: false,
            showForgotPasswordModal: false,
            loginChallenge: null,
        };
    }
    try {
        const accessToken = localStorage.getItem(ACCESS_KEY);
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        const rawUser = localStorage.getItem(USER_KEY);
        const user = rawUser ? JSON.parse(rawUser) : null;
        const hasTokens = Boolean(accessToken && refreshToken);
        const isAuthenticated = Boolean(hasTokens && user);
        return {
            isAuthenticated,
            user,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
            isLoading: !isAuthenticated,
            error: null,
            showLoginModal: !isAuthenticated,
            showRegisterModal: false,
            showForgotPasswordModal: false,
            loginChallenge: null,
        };
    } catch {
        return {
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            isLoading: true,
            error: null,
            showLoginModal: true,
            showRegisterModal: false,
            showForgotPasswordModal: false,
            loginChallenge: null,
        };
    }
}

function persistUser(user) {
    if (IS_WEB) return; // user lives in mg_user cookie on web
    try {
        if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
        else localStorage.removeItem(USER_KEY);
    } catch { /* quota / private mode — ignore */ }
}

const authSlice = createSlice({
    name: 'auth',
    initialState: hydrateInitialState(),
    reducers: {
        setAuthenticated(state, action) {
            state.isAuthenticated = action.payload;
        },
        setUser(state, action) {
            state.user = action.payload;
            persistUser(action.payload);
        },
        setAccessToken(state, action) {
            state.accessToken = action.payload;
        },
        setRefreshToken(state, action) {
            state.refreshToken = action.payload;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setShowLoginModal(state, action) {
            state.showLoginModal = action.payload;
        },
        setShowRegisterModal(state, action) {
            state.showRegisterModal = action.payload;
        },
        setShowForgotPasswordModal(state, action) {
            state.showForgotPasswordModal = action.payload;
        },
        // A sign-in that stopped at the email-code step: { challengeId, email,
        // expiresIn, sentAt }. Null once verified, cancelled, or signed out.
        setLoginChallenge(state, action) {
            state.loginChallenge = action.payload;
        },
        clearAuth(state) {
            state.isAuthenticated = false;
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.error = null;
            state.isLoading = false;
            state.loginChallenge = null;
            persistUser(null);
        },
    },
});

export const {
    setAuthenticated,
    setUser,
    setAccessToken,
    setRefreshToken,
    setIsLoading,
    setError,
    setShowLoginModal,
    setShowRegisterModal,
    setShowForgotPasswordModal,
    setLoginChallenge,
    clearAuth,
} = authSlice.actions;

export default authSlice.reducer;
